import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import AddDiseaseMapping from '../components/AddDiseaseMapping';
import { getCropDiseaseMappings, getCropDiseaseMappingById } from '../lib/api';

const customStyles = `
  .dataTables_wrapper {
    padding: 0 !important;
    border: none !important;
    background: none !important;
  }
  .dataTables_wrapper .dataTables_filter,
  .dataTables_wrapper .dataTables_paginate,
  .dataTables_wrapper .dataTables_info,
  .dataTables_wrapper .dataTables_length {
    display: none !important;
  }
  .tooltip-menu {
    min-width: 140px;
    padding: 0.5rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    background: #fff;
    z-index: 1000;
  }
  .tooltip-item {
    padding: 0.5rem 1rem; cursor: pointer; font-size: 0.875rem; color: #4a4a4a;
  }
  .tooltip-item:hover { background-color: #f1f5f9; color: #1a73e8; }
`;

const DiseaseMappingList = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [diseaseMappings, setDiseaseMappings] = useState([]);
  const [diseaseMappingToEdit, setDiseaseMappingToEdit] = useState(null);
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef(null);

  const fetchDiseaseMappings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await getCropDiseaseMappings(token);

      if (response && response.data) {
        const transformedData = response.data.map((item, index) => ({
          id: item.CropDiseasesID || item.CropID || index + 1,
          crop: item.CropName || 'N/A',
          disease: item.DiseaseNames || 'No diseases mapped',
          originalData: item,
        }));
        setDiseaseMappings(transformedData);
      } else {
        setDiseaseMappings([]);
      }
    } catch (err) {
      console.error('Error fetching disease mappings:', err);
      setDiseaseMappings([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleDrawer = (payload = null) => {
    setDiseaseMappingToEdit(payload);
    setIsDrawerOpen((prev) => !prev);
  };

  const handleSubmitSuccess = async () => {
    await fetchDiseaseMappings();
  };

  const handleEdit = async (row) => {
    try {
      const token = localStorage.getItem('authToken');
      // Fetch full details for this CropDiseasesID
      const full = await getCropDiseaseMappingById(token, row.originalData?.CropDiseasesID || row.id);
      // Pass the fully-detailed payload to the drawer
      toggleDrawer(full?.data || null);
    } catch (err) {
      console.error('Error fetching full mapping for edit:', err);
      // Fallback to open drawer with row (limited edit)
      toggleDrawer(row);
    }
  };

  useEffect(() => {
    fetchDiseaseMappings();
  }, []);

  const totalPages = showEntries === -1 ? 1 : Math.max(1, Math.ceil(diseaseMappings.length / showEntries));
  const startIndex = showEntries === -1 ? 0 : (currentPage - 1) * showEntries;
  const endIndex = showEntries === -1 ? diseaseMappings.length : Math.min(startIndex + showEntries, diseaseMappings.length);
  const currentData = (showEntries === -1 ? diseaseMappings : diseaseMappings.slice(startIndex, endIndex));

  const handlePreviousPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));
  const handlePageChange = (page) => setCurrentPage(page);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    const token = localStorage.getItem('authToken');
    if (!term) {
      // Reset by refetching originals to avoid double-filtering artifacts
      fetchDiseaseMappings(token);
      return;
    }
    const filtered = diseaseMappings.filter(
      (m) => m.crop.toLowerCase().includes(term) || m.disease.toLowerCase().includes(term)
    );
    setDiseaseMappings(filtered);
    setCurrentPage(1);
  };

  return (
    <>
      <style>{customStyles}</style>

      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="custom-select"
              value={showEntries}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setShowEntries(v);
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>

            <div className="navbar-search">
              <div className="relative">
                <input
                  type="text"
                  className="form-control h-[36px] min-w-[220px] pl-10 pr-3 text-sm border border-neutral-300 radius-6 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  name="search"
                  placeholder="Search crops or diseases..."
                  ref={searchInputRef}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>

          <button
            onClick={() => toggleDrawer(null)}
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New Disease Mapping
          </button>
        </div>

        <div className="card h-100 p-16 radius-12">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading disease mappings...</p>
            </div>
          ) : diseaseMappings.length === 0 ? (
            <div className="text-center py-4">
              <Icon icon="mdi:file-document-outline" className="text-muted" style={{ fontSize: '48px' }} />
              <p className="text-muted mt-2">No disease mappings found.</p>
              <button className="btn btn-primary mt-2" onClick={() => toggleDrawer(null)}>
                Add Your First Disease Mapping
              </button>
            </div>
          ) : (
            <>
            
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Crop</th>
                      <th scope="col">Disease</th>
                      <th scope="col" className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((mapping) => (
                      <tr key={mapping.id}>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {mapping.crop}
                          </span>
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {mapping.disease}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                            <button
                              type="button"
                              className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(mapping)}
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {showEntries !== -1 && (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                  <span>
                    Showing {startIndex + 1} to {endIndex} of {diseaseMappings.length} entries
                  </span>
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <Icon icon="ep:d-arrow-left" />
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index} className="page-item">
                        <button
                          className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                            currentPage === index + 1 ? 'bg-primary-600 text-white' : ''
                          }`}
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages}
                      >
                        <Icon icon="ep:d-arrow-right" />
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <AddDiseaseMapping
        isOpen={isDrawerOpen}
        onClose={() => {
          setDiseaseMappingToEdit(null);
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmitSuccess}
        diseaseMappingToEdit={diseaseMappingToEdit}
        onSuccess={fetchDiseaseMappings}
      />
    </>
  );
};

export default DiseaseMappingList;
